/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { CosmosButton, CosmosCard, CosmosLink, CosmosTag, CosmosText, EdsIcon } from "@components/elements";
import type { CosmosButtonProps } from "@components/elements/atoms/button/CosmosButton.atom";
import type { EdsIconType } from "@components/elements/atoms/eds-icon/EdsIcon.type";
import { useTranslation } from "@epic-mw/i18n";
import { css, cva, cx } from "@panda/css";
import { Box, Flex, HStack, VStack } from "@panda/jsx";
import { token } from "@panda/tokens";

/**
 * Tags beyond this count collapse into a single "+ N more" overflow chip,
 * keeping every card's tag rows to a uniform two-pill height in the grid.
 */
const MAX_VISIBLE_TAGS = 2;

/* -------------------------------------------------------------------------- *
 * Public types
 * -------------------------------------------------------------------------- */

/**
 * Logo treatment for the 80×80 circle on the left of the card.
 *
 * - `image` — full-bleed company logo (Training Partner).
 * - `icon`  — EDS icon illustration on a tinted backdrop. Tone follows `badge.tone`.
 * - omitted — no logo column, title + meta + tags shift left to align with
 *             the card padding (Gold Instructor without a profile photo).
 */
// Discriminant `kind` aligns with the rest of the Directory unions
// (`FilterDef.kind`, `SortOption.kind`, `SearchAccessor.type` is the
// remaining outlier — kept because `SearchAccessor` shipped before this
// convention took hold and renaming it is a downstream break).
export type DirectoryCardLogo = { kind: "image"; src: string; alt?: string } | { kind: "icon"; icon: EdsIconType };

/**
 * Visual tone of the badge pill + logo container.
 *
 * - `gold` — warm yellow→orange gradient. For "Gold instructor" / "Gold partner"
 *            variants where the gold gradient is brand-meaningful.
 * - `neutral` — cool transparent-white. For "Academic partner" and any non-gold
 *               variants where the warm gold would be misleading.
 *
 * Default: `gold`.
 */
export type DirectoryCardTone = "gold" | "silver" | "bronze" | "no-badge" | "neutral";

/**
 * Pill badge anchored to the top of the right column.
 *
 * Defaults render the Unreal mark to the left of the label. Pass `icon` to
 * swap the default mark for another EDS icon. `tone` controls both the badge
 * mini-mark AND the logo container styling (they stay in sync, per Figma).
 */
export type DirectoryCardBadge = {
	label: string;
	tone?: DirectoryCardTone;
	icon?: EdsIconType;
};

/**
 * Heading level for the card's name. Defaults to `h3` (cards usually live
 * under an `h2` section heading); pass a different level if the surrounding
 * page hierarchy requires it.
 */
export type DirectoryCardHeadingLevel = "h2" | "h3" | "h4";

/**
 * Single item in a meta row. Items render in a CSS grid so icons align
 * vertically across rows. When `href` is set the label renders as an
 * underlined link.
 */
export type DirectoryCardMetaItem = {
	icon?: EdsIconType;
	label: string;
	href?: string;
};

interface DirectoryCardBaseProps {
	name: string;

	/** Pill rendered above the name. */
	badge?: DirectoryCardBadge;

	/** Optional 80×80 logo / icon-illustration on the left of the card. */
	logo?: DirectoryCardLogo;

	/**
	 * First meta row — typically underlined links such as Website + Email.
	 * Empty/omitted hides the row.
	 */
	links?: DirectoryCardMetaItem[];

	/**
	 * Second meta row — typically plain text such as Country + Language.
	 * Empty/omitted hides the row.
	 */
	meta?: DirectoryCardMetaItem[];

	/** Primary tags ("Tag Link" in Figma — filled background). */
	primaryTags?: string[];

	/** Secondary tags ("Tag Static" in Figma — outlined). */
	secondaryTags?: string[];

	/** Action buttons rendered at the bottom of the card, separated by a divider. */
	buttons?: CosmosButtonProps[];

	/**
	 * Heading element rendered for the name. Defaults to `h3`.
	 */
	headingLevel?: DirectoryCardHeadingLevel;

	/**
	 * Class applied to the outer `CosmosCard` surface. The card also exposes a
	 * stable external selector hook of `.directory-card`.
	 */
	className?: string;
}

/**
 * `href` + `hrefLabel` form a discriminated chevron contract: setting one
 * requires the other. The chevron link's accessible name MUST announce the
 * destination ("View Acme partner profile") — falling back to the card's
 * name alone leaves AT users without verb / destination context. Reserved
 * for the future Service Partner clickable variant — Gold Instructor /
 * Training Partner adapters omit both fields.
 *
 * Absolute URLs (`http(s)://…`) automatically open in a new tab with
 * `rel="noopener noreferrer"`; same-origin paths follow CosmosLink defaults.
 */
export type DirectoryCardProps = DirectoryCardBaseProps &
	({ href: string; hrefLabel: string } | { href?: never; hrefLabel?: never });

/* -------------------------------------------------------------------------- *
 * Tone-driven style fragments
 * -------------------------------------------------------------------------- */

// Soft gradient backdrop for the logo circle (10% alpha per Figma).
const goldLogoBg = "linear-gradient(225deg, rgba(251, 237, 156, 0.10) 0%, rgba(254, 133, 57, 0.10) 100%)";
const silverLogoBg = "linear-gradient(225deg, rgba(240, 240, 240, 0.10) 0%, rgba(97, 97, 97, 0.10) 100%)";
const bronzeLogoBg = "linear-gradient(225deg, rgba(255, 175, 166, 0.10) 0%, rgba(126, 77, 71, 0.10) 100%)";

const logoBgMap: Partial<Record<DirectoryCardTone, string>> = {
	gold: goldLogoBg,
	silver: silverLogoBg,
	bronze: bronzeLogoBg,
};

// Full-strength gradient for the badge mini-mark (100% alpha per Figma).
const goldMarkBg = "linear-gradient(225deg, #FBED9C 0%, #FE8539 100%)";
const silverMarkBg = "linear-gradient(225deg, #F0F0F0 0%, #616161 100%)";
const bronzeMarkBg = "linear-gradient(225deg, #FFAFA6 0%, #7E4D47 100%)";

const ringGradientDefs: Partial<Record<DirectoryCardTone, { id: string; stops: [string, string] }>> = {
	gold: { id: "cosmosDirectoryCardRingGold", stops: ["#FBED9C", "#FE8539"] },
	silver: { id: "cosmosDirectoryCardRingSilver", stops: ["#F0F0F0", "#616161"] },
	bronze: { id: "cosmosDirectoryCardRingBronze", stops: ["#FFAFA6", "#7E4D47"] },
};
/* -------------------------------------------------------------------------- *
 * Card surface + content
 * -------------------------------------------------------------------------- */

const cardStyles = css({
	minW: "0",
	height: "full",
	// Hover styling intentionally NOT applied to the entire card surface — only
	// the chevron link in the top-right is interactive when `href` is set, so
	// surfacing a card-wide hover would misrepresent the click target.
});

/* ---------- top row: optional logo + (badge + name) + optional chevron ---- */
const overFlowHidden = css({
	overflow: "hidden",
	position: "relative",
});

const topRowStyles = css({
	display: "flex",
	gap: "eds.dimension.16",
	alignItems: "center",
	width: "full",
	xsDown: {
		flexDirection: "column",
		alignItems: "start",
	},
});

/**
 * Logo container — exact Figma measurements from node 76:8856:
 *   - 88×88 outer frame (`logoContainerStyles`)
 *   - 80×80 inner backdrop circle inset 4px (`logoInnerBase`)
 *   - 38×38 icon centered inside the 80×80 (so 21px gap from icon to ring)
 *   - Ring rendered via inline SVG `<circle stroke="url(#…)" />` — vector
 *     rendering avoids the blocky `mask-composite` artifacts that plagued
 *     earlier CSS-only attempts.
 *
 * For image logos (partners), `padding: 12px` inside the 80×80 inner
 * leaves a small visible gap from the ring while letting the logo
 * dominate. Icon variant uses no padding — the icon is intrinsically
 * smaller (38 vs 80) so the gap is from `justify/align center`.
 */
const logoContainerStyles = css({
	position: "relative",
	flexShrink: 0,
	width: "120px",
	height: "120px",
});

const logoInnerBase = css({
	position: "absolute",
	inset: "6px",
	width: "108px",
	height: "108px",
	borderRadius: "50%",
	overflow: "hidden",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	"& svg": {
		width: "52px!",
		height: "52px!",
	},
	"& img": {
		width: "full",
		height: "full",
		objectFit: "contain",
		display: "block",
		// Padding inside the backdrop so partner logos don't touch the ring.
		// Icons (38×38) already have 21px breathing room via center-alignment;
		// images need explicit padding to achieve a similar gap.
		padding: "eds.dimension.12",
	},
});

const logoRingSvgStyles = css({
	position: "absolute",
	inset: 0,
	width: "120px",
	height: "120px",
	pointerEvents: "none",
});

// Gold tone — warm yellow→orange backdrop + gradient stroke ring.
const logoInnerGold = css({
	color: "eds.palette.orange.400",
});

// Neutral tone — transparent-white backdrop + subtle solid stroke ring.
const logoInnerNeutral = css({
	backgroundColor: "eds.palette.transparent.light.015",
	color: "eds.text.primary",
	backdropFilter: "blur(37.5px)",
});

const titleColumnStyles = css({
	display: "flex",
	flexDirection: "column",
	gap: "eds.dimension.16",
	flex: "1 1 auto",
	minWidth: "0",
	alignSelf: "center",
	xsDown: {
		alignSelf: "start",
	},
});

const nameStyles = css({
	color: "eds.text.primary",
	textWrap: "balance",
});

/* ---------- badge pill ---------------------------------------------------- */
// Exact Figma 76:8862 spec: 16×16 box, 4.571px radius, 0.286px subtle white
// border, gradient backdrop. Icon (76:8863) at 9.429×9.429 — needs `!important`
// to win over EdsIcon's inline `size="xs"` width/height attributes (the
// wrapper's prop sets the SVG width attribute directly, which CSS without
// `!important` can't override).
const badgeMarkBase = css({
	width: "32px",
	height: "32px",
	borderRadius: "9.14px",
	overflow: "hidden",
	display: "inline-flex",
	alignItems: "center",
	justifyContent: "center",
	flexShrink: 0,
	alignSelf: "start",
	"& svg": {
		width: "27px !important",
		height: "27px !important",
	},
});

const badgeMark = cva({
	variants: {
		tone: {
			gold: {
				background: goldMarkBg,
			},
			silver: {
				background: silverMarkBg,
			},
			bronze: {
				background: bronzeMarkBg,
			},
			"no-badge": {
				backgroundColor: "transparent",
				color: "transparent",
				"& svg": { color: "transparent" },
			},
			neutral: {
				backgroundColor: "eds.palette.grayscale.gray080",
				color: "eds.text.primary",
				borderWidth: "0.571px",
				borderStyle: "solid",
				borderColor: "rgba(255, 255, 255, 0.15)",
			},
		},
	},
	base: {
		color: "#1A1106",
		"& svg": { color: "#1A1106" },
	},
});

// Badge mark overlaid at bottom-right of the logo circle (Figma node 195:25434–35).
const badgeOnLogoContainerStyles = css({
	position: "absolute",
	right: "1.67px",
	top: "93px",
	display: "inline-flex",
	alignItems: "center",
	justifyContent: "center",
});

/* ---------- meta rows — shared-column grid for vertical icon alignment ----- */

/**
 * Meta section renders as a single CSS grid whose columns are auto-sized
 * across ALL rows. Each row places its items at the same column positions
 * (item, dot, item, dot, item, …), so icons in column 1 of row 1 vertically
 * align with icons in column 1 of row 2, etc. — matching the Figma layout.
 *
 * The grid template width is `auto auto auto … auto` with `2 × maxItems - 1`
 * columns, where `maxItems` is the max item count across the rows passed in.
 * Variable row lengths (2 links + 1 meta, or 3 links + 2 meta) are handled
 * by rendering empty placeholder cells for shorter rows — column widths stay
 * stable across the whole meta block.
 */
const metaGridBaseStyles = css({
	display: "flex",
	flexWrap: "wrap",
	columnGap: "eds.dimension.16",
	rowGap: "eds.dimension.4",
	alignItems: "center",
	width: "full",
	xsDown: {
		flexDirection: "column",
		alignItems: "start",
	},
});

const metaItemStyles = css({
	display: "inline-flex",
	alignItems: "center",
	gap: "eds.dimension.6",
	color: "eds.text.primary",
	"& svg": {
		alignSelf: "start",
		justifySelf: "start",
		flexShrink: 0,
	},
	xsDown: {
		alignItems: "start",
	},
	"&:not(:first-child)": {
		_before: {
			position: "absolute",
			content: "'·'",
			display: "block",
			color: "eds.text.secondary",
			marginInlineStart: "-eds.dimension.10",
			minWidth: "eds.dimension.4",
			textAlign: "center",
			verticalAlign: "middle",
			xsDown: {
				display: "none",
			},
		},
	},
});

const metaLinkStyles = css({
	textDecoration: "underline",
	textDecorationSkipInk: "none",
	color: "eds.text.primary",
	transition: "opacity 160ms ease-out",
	"&:hover, &:focus-visible": {
		color: "eds.text.primary",
		opacity: 0.85,
	},
});

/* ---------- tag rows ------------------------------------------------------ */

const tagsSectionStyles = css({
	display: "flex",
	flexDirection: "column",
	gap: "eds.dimension.8",
	width: "full",
});

const tagsRowStyles = css({
	display: "flex",
	flexWrap: "wrap",
	gap: "eds.dimension.8",
});

const tagPillStyles = css({
	whiteSpace: "nowrap",
	maxW: "full",
	overflow: "hidden",
	"& *": {
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
});

const tagPillOutlined = css({
	borderWidth: "1px",
	borderStyle: "solid",
	borderColor: "eds.palette.transparent.light.035",
});

/* ---------- chevron link (Service Partner — clickable variant) ----------- */

const chevronButtonStyles = css({
	flexShrink: 0,
	width: "40px",
	height: "40px",
	borderRadius: "9999px",
	display: "inline-flex",
	alignItems: "center",
	justifyContent: "center",
	backgroundColor: "eds.palette.transparent.light.015",
	color: "eds.text.primary",
	transition: "background-color 160ms ease-out, transform 160ms ease-out",
	"&:hover": {
		backgroundColor: "eds.palette.transparent.light.035",
		color: "eds.text.primary",
	},
	"&:focus-visible": {
		backgroundColor: "eds.palette.transparent.light.035",
		color: "eds.text.primary",
		outlineWidth: "2px",
		outlineStyle: "solid",
		// Solid color — `transparent.light.075` (~7.5% white) failed WCAG 3:1
		// against the blurred card surface; `eds.text.primary` is full-alpha
		// and audited for AA contrast on every Cosmos background.
		outlineColor: "eds.text.primary",
		outlineOffset: "2px",
	},
	"@media (prefers-reduced-motion: no-preference)": {
		"&:hover": {
			transform: "translateX(2px)",
		},
	},
	"& svg": {
		width: "20px",
		height: "20px",
	},
});

/* ---------- action buttons (Learn more / Contact) ------------------------- */

const actionDividerStyles = css({
	width: "full",
	borderTopWidth: "1px",
	borderTopStyle: "solid",
	borderTopColor: "eds.border.subtle",
});

const actionRowStyles = css({
	display: "flex",
	gap: "eds.dimension.16",
	alignItems: "start",
	width: "full",
	flexWrap: "wrap",
});

const actionButtonStyles = css({
	maxW: "full",
	alignItems: "start",
});

/* -------------------------------------------------------------------------- *
 * Sub-components
 * -------------------------------------------------------------------------- */

function DirectoryCardLogoEl({
	logo,
	tone,
	badge,
}: {
	logo: DirectoryCardLogo;
	tone: DirectoryCardTone;
	badge?: DirectoryCardBadge;
}): React.JSX.Element {
	const innerClass = tone === "gold" ? logoInnerGold : logoInnerNeutral;
	const logoBackground = logoBgMap[tone] ? { background: logoBgMap[tone] } : undefined;
	const ringGradient = ringGradientDefs[tone];

	return (
		<div className={logoContainerStyles}>
			<div className={cx(logoInnerBase, innerClass)} style={logoBackground}>
				{logo.kind === "image" ? (
					<img src={logo.src} alt={logo.alt ?? ""} width={109} height={109} loading="lazy" decoding="async" />
				) : (
					<EdsIcon icon={logo.icon} aria-hidden="true" />
				)}
			</div>
			{/*
				Ring rendered as SVG `<circle stroke=…>` matching Figma node 76:8859
				(88×88 outer ellipse). Vector stroke is crisp at any DPR; supports
				the linear gradient natively.
			*/}
			<svg
				xmlns="http://www.w3.org/2000/svg"
				className={logoRingSvgStyles}
				width="123"
				height="123"
				viewBox="0 0 123 123"
				fill="none"
			>
				<circle
					cx="61.3638"
					cy="61.3636"
					r="60"
					stroke={ringGradient ? `url(#${ringGradient.id})` : "rgba(255, 255, 255, 0.15)"}
					strokeWidth="2.72727"
				/>
				{ringGradient ? (
					<defs>
						<linearGradient
							id={ringGradient.id}
							x1="108.5"
							y1="14.5"
							x2="14.5"
							y2="108.5"
							gradientUnits="userSpaceOnUse"
						>
							<stop stopColor={ringGradient.stops[0]} />
							<stop offset="1" stopColor={ringGradient.stops[1]} />
						</linearGradient>
					</defs>
				) : null}
			</svg>
			{badge && badge.tone !== "no-badge" ? (
				<span className={badgeOnLogoContainerStyles} aria-hidden="true">
					<span className={cx(badgeMarkBase, badgeMark({ tone }))}>
						<EdsIcon icon={badge.icon ?? "UnrealEngine"} size="xs" aria-hidden="true" />
					</span>
				</span>
			) : null}
		</div>
	);
}

export type DirectoryCardBadgeElProps = { badge: DirectoryCardBadge; className?: string };
export function DirectoryCardBadgeEl({ badge, className }: DirectoryCardBadgeElProps): React.JSX.Element | null {
	if (badge.tone === "no-badge") return null;
	const tone = badge.tone ?? "neutral";
	const markIcon: EdsIconType = badge.icon ?? "UnrealEngine";

	return (
		<span className={cx("directory-card-badge", className, badgeMarkBase, badgeMark({ tone }))} aria-hidden="true">
			<EdsIcon icon={markIcon} size="sm" aria-hidden="true" />
		</span>
	);
}

const isAbsoluteUrl = (href: string): boolean => /^https?:\/\//i.test(href);

function MetaItemEl({ item }: { item: DirectoryCardMetaItem }): React.JSX.Element {
	const label = (
		<CosmosText variant="uiMD" color="eds.text.primary">
			{item.label}
		</CosmosText>
	);
	const external = item.href ? isAbsoluteUrl(item.href) : false;
	return (
		<span className={cx("meta-item", metaItemStyles)}>
			{item.icon ? <EdsIcon icon={item.icon} size="sm" aria-hidden="true" color="secondary" /> : null}
			{item.href ? (
				<CosmosLink
					href={item.href}
					className={metaLinkStyles}
					{...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
				>
					{label}
				</CosmosLink>
			) : (
				label
			)}
		</span>
	);
}

export function DirectoryCardMetaGrid({ items }: { items: DirectoryCardMetaItem[] }): React.JSX.Element {
	return (
		<Box className={metaGridBaseStyles}>
			{items.map((item, i) => (
				<MetaItemEl item={item} key={i} />
			))}
		</Box>
	);
}
type DirectoryCardTagRowElProps = {
	tags: string[];
	variant: "filled" | "outlined";
	maxVisibleTags?: number;
};

function DirectoryCardTagRowEl({ tags, variant, maxVisibleTags }: DirectoryCardTagRowElProps): React.JSX.Element {
	const t = useTranslation();

	const visibleTags = maxVisibleTags && maxVisibleTags > 0 ? tags.slice(0, maxVisibleTags) : tags;
	const overflowCount = tags.length - visibleTags.length;
	// t() can return string | string[] depending on interpolation; normalize to string.
	const overflowLabel = overflowCount > 0 ? [t("epic.cosmos.plus.x.more", [overflowCount])].flat().join("") : null;

	return (
		<div className={tagsRowStyles}>
			{visibleTags.map((tag) => (
				<CosmosTag
					key={tag}
					label={tag}
					size="uiSM"
					color={token("colors.eds.text.primary")}
					backgroundColor={variant === "filled" ? token("colors.eds.palette.transparent.light.015") : "transparent"}
					className={cx(tagPillStyles, variant === "outlined" ? tagPillOutlined : undefined)}
				/>
			))}
			{overflowLabel ? (
				<CosmosTag
					label={overflowLabel}
					size="uiSM"
					color={token("colors.eds.text.primary")}
					backgroundColor={variant === "filled" ? token("colors.eds.palette.transparent.light.015") : "transparent"}
					className={cx(tagPillStyles, variant === "outlined" ? tagPillOutlined : undefined)}
				/>
			) : null}
		</div>
	);
}

type DirectoryCardTagsSectionProps = {
	/** Primary tags ("Tag Link" in Figma — filled background). */
	primaryTags?: string[];
	/** Secondary tags ("Tag Static" in Figma — outlined). */
	secondaryTags?: string[];
	maxVisibleTags?: number;
};
/**
 * Tag block shared by `DirectoryCard` and the Service Partner detail view:
 * an optional filled (primary) row above an optional outlined (secondary)
 * row, wrapped in the `tagsSectionStyles` container. Renders `null` when both
 * lists are empty so callers can drop it in unconditionally.
 */
export function DirectoryCardTagsSection({
	primaryTags = [],
	secondaryTags = [],
	maxVisibleTags,
}: DirectoryCardTagsSectionProps): React.JSX.Element | null {
	const hasPrimary = primaryTags.length > 0;
	const hasSecondary = secondaryTags.length > 0;
	if (!hasPrimary && !hasSecondary) return null;

	return (
		<Box className={tagsSectionStyles}>
			{hasPrimary ? (
				<DirectoryCardTagRowEl tags={primaryTags} variant="filled" maxVisibleTags={maxVisibleTags} />
			) : null}
			{hasSecondary ? (
				<DirectoryCardTagRowEl tags={secondaryTags} variant="outlined" maxVisibleTags={maxVisibleTags} />
			) : null}
		</Box>
	);
}

/* -------------------------------------------------------------------------- *
 * DirectoryCard
 * -------------------------------------------------------------------------- */

export function DirectoryCard({
	name,
	badge,
	logo,
	links = [],
	meta = [],
	primaryTags = [],
	secondaryTags = [],
	buttons = [],
	headingLevel = "h3",
	href,
	hrefLabel,
	className,
}: DirectoryCardProps): React.JSX.Element {
	const clickable = Boolean(href);
	const tone = badge?.tone ?? "neutral";
	const chevronExternal = href ? isAbsoluteUrl(href) : false;

	const metaRows: DirectoryCardMetaItem[][] = [];
	if (links.length > 0) metaRows.push(links);
	if (meta.length > 0) metaRows.push(meta);

	return (
		<CosmosCard
			className={cx("directory-card", cardStyles, className)}
			variant="materialLight"
			borderRadius={24}
			data-clickable={clickable ? "true" : "false"}
		>
			<CosmosCard.Content height="full">
				<VStack gap="eds.dimension.20" height="full" width="full" justifyContent="space-between">
					<VStack gap="eds.dimension.20" width="full">
						{/* Top row: optional logo + (badge + name column) + optional chevron link */}
						<Flex className={cx(topRowStyles, overFlowHidden)} minH={logo ? "140px" : undefined}>
							{logo ? <DirectoryCardLogoEl logo={logo} tone={tone} badge={badge} /> : null}
							<Box className={cx(titleColumnStyles, logo ? overFlowHidden : null)}>
								<VStack gap="eds.dimension.8" alignItems="start">
									<HStack gap="eds.dimension.8" alignItems="center">
										{!logo && badge ? <DirectoryCardBadgeEl badge={badge} /> : null}
										<CosmosText variant="headingMD" as={headingLevel} className={nameStyles}>
											{name}
										</CosmosText>
									</HStack>
									{/* Meta grid — links row above plain-meta row, icons align across columns */}
									{links.length > 0 ? <DirectoryCardMetaGrid items={links} /> : null}
									{meta.length > 0 ? <DirectoryCardMetaGrid items={meta} /> : null}
								</VStack>
							</Box>
							{href ? (
								<CosmosLink
									href={href}
									className={chevronButtonStyles}
									aria-label={hrefLabel ?? name}
									{...(chevronExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
								>
									<EdsIcon icon="ChevronRight" aria-hidden="true" />
								</CosmosLink>
							) : null}
						</Flex>

						{/* Tag rows: primary (filled) + optional secondary (outlined). */}
						<DirectoryCardTagsSection
							primaryTags={primaryTags}
							secondaryTags={secondaryTags}
							maxVisibleTags={MAX_VISIBLE_TAGS}
						/>
					</VStack>

					{/* Action buttons rendered below a divider. */}
					{buttons.length > 0 ? (
						<VStack gap="eds.dimension.20" width="full">
							<hr className={actionDividerStyles} />
							<Box className={actionRowStyles}>
								{buttons.map((btnProps, i) => (
									<CosmosButton key={i} iconSize="sm" className={actionButtonStyles} {...btnProps} />
								))}
							</Box>
						</VStack>
					) : null}
				</VStack>
			</CosmosCard.Content>
		</CosmosCard>
	);
}
